import { RealtimeChannel, type ChannelHost } from "./channel";
import { RealtimeConnection } from "./connection";
import type {
  RealtimeClient,
  RealtimeClientOptions,
  RealtimeEngineOptions,
  RealtimeFrame,
} from "./types";

import { baseURL, doRefresh, isTokenExpired } from "@/lib/axios/instance";

const DEFAULT_HUB_PATH = "/hubs/realtime";

async function defaultAccessToken(): Promise<string> {
  const token = localStorage.getItem("accessToken");
  if (!token) throw new Error("Not authenticated: no access token to open a realtime connection with");
  return isTokenExpired(token) ? doRefresh() : token;
}

export function createRealtimeClient(options?: RealtimeClientOptions): RealtimeClient {
  const resolved: RealtimeEngineOptions = {
    url: options?.url ?? baseURL,
    hubPath: options?.hubPath ?? DEFAULT_HUB_PATH,
    accessToken: options?.accessToken ?? defaultAccessToken,
  };
  return new RealtimeEngine(resolved);
}

type TopicMembership = {
  refCount: number;
  handle?: string;
  joinPromise?: Promise<void>;
  channels: Set<RealtimeChannel>;
};

const canonicalTopic = (topic: string): string => topic.trim().toLowerCase();

export class RealtimeEngine implements ChannelHost {
  private readonly connection: RealtimeConnection;
  private readonly memberships = new Map<string, TopicMembership>();

  constructor(options: RealtimeEngineOptions) {
    this.connection = new RealtimeConnection(
      options,
      (frame) => this.routeFrame(frame),
      () => this.replayAll(),
      () => this.notifyPermanentClose(),
    );
  }

  channel(topic: string): RealtimeChannel {
    return new RealtimeChannel(canonicalTopic(topic), this);
  }

  async disconnect(): Promise<void> {
    this.memberships.clear();
    await this.connection.stop();
  }

  async acquire(topic: string, channel: RealtimeChannel): Promise<void> {
    const membership = this.memberships.get(topic) ?? this.createMembership(topic);
    membership.channels.add(channel);
    membership.refCount += 1;

    if (membership.refCount === 1) {
      membership.joinPromise = this.connection.join(topic).then((handle) => {
        membership.handle = handle;
      });
    }

    try {
      await membership.joinPromise;
    } catch (err) {
      membership.channels.delete(channel);
      membership.refCount -= 1;
      if (membership.refCount === 0) this.memberships.delete(topic);
      throw err;
    }
  }

  async release(topic: string, channel: RealtimeChannel): Promise<void> {
    const membership = this.memberships.get(topic);
    if (!membership || !membership.channels.has(channel)) return;

    membership.channels.delete(channel);
    membership.refCount -= 1;
    if (membership.refCount > 0) return;

    try {
      await membership.joinPromise;
    } catch {
      // Join failed — nothing to leave.
    }

    if (membership.refCount === 0 && this.memberships.get(topic) === membership) {
      this.memberships.delete(topic);
      if (membership.handle) await this.connection.leave(membership.handle);
    }
  }

  private createMembership(topic: string): TopicMembership {
    const membership: TopicMembership = { refCount: 0, channels: new Set() };
    this.memberships.set(topic, membership);
    return membership;
  }

  private routeFrame(frame: RealtimeFrame): void {
    const membership = this.memberships.get(canonicalTopic(frame.topic ?? ""));
    if (!membership) return;
    for (const channel of membership.channels) channel.dispatch(frame);
  }

  private notifyPermanentClose(): void {
    const error = new Error("Realtime connection closed permanently");
    for (const membership of this.memberships.values()) {
      for (const channel of membership.channels) channel.notifyReconnectError(error);
    }
  }

  private replayAll(): void {
    for (const [topic, membership] of this.memberships) {
      membership.joinPromise = this.rejoin(topic, membership);
    }
  }

  private async rejoin(topic: string, membership: TopicMembership): Promise<void> {
    try {
      membership.handle = await this.connection.join(topic);
    } catch (err) {
      membership.handle = undefined;
      const error = err instanceof Error ? err : new Error(String(err));
      for (const channel of membership.channels) channel.notifyReconnectError(error);
      return;
    }

    for (const channel of membership.channels) channel.notifyResubscribed();
  }
}

export const realtimeClient = createRealtimeClient();
